import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'dibs-react-intl';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { trackNullSearchRelatedItemProvider } from './sbSharedNullSearchRelatedItemsTrackingHelpers';
import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useShowItemLocationBadge } from '../../shared/hooks/useShowItemLocationBadge';

// components
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { SbSharedSearchSuggestions } from '../SbSharedSearchSuggestions/SbSharedSearchSuggestions';
import { SbSharedNullSearchRelatedItemsFollowLazy } from './SbSharedNullSearchRelatedItemsFollowLazy';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';

// styles
import styles from './SbSharedNullSearchRelatedItems.scss';

export const SbSharedNullSearchRelatedItemsComponent = ({
    user,
    relay,
    viewer,
    isMobile,
    isClient,
    itemSearch,
    searchTerm,
    verticalOffset,
    shouldFetchFolder,
    lazyLoadStartIndex,
}) => {
    const attemptedToTrackItemIndexes = useRef([]);
    const { regionalInfo } = viewer;
    const userId = user ? user.serviceId : null;
    const { relatedSearchResults } = itemSearch;
    const { edges: nodes = [] } = relatedSearchResults;
    const hasRelatedSearchItems = !!nodes.length;
    const userRegions = getUserSessionRegions() || regionalInfo?.[0]?.regionsByZipCode || [];
    const showItemLocationBadge = useShowItemLocationBadge();

    const [measurementUnit] = useMeasurementUnit({
        user,
        itemSearch,
    });

    const { current: impressionTracking } = useRef(
        hasRelatedSearchItems
            ? trackNullSearchRelatedItemProvider({ nodes, searchTerm, userRegions })
            : {}
    );
    const {
        trackNullSearchRelatedItemsLoad = () => {},
        trackNullSearchRelatedItemClick = () => {},
        trackNullSearchRelatedItemsImpressions = () => {},
    } = impressionTracking;

    useEffect(() => {
        trackNullSearchRelatedItemsLoad();
    }, [trackNullSearchRelatedItemsLoad]);

    const onContentVisible = index => {
        if (!regionalInfo) {
            attemptedToTrackItemIndexes.current.push(index);
        } else {
            attemptedToTrackItemIndexes.current.forEach(attemptedToTrackItemIndex => {
                trackNullSearchRelatedItemsImpressions({ index: attemptedToTrackItemIndex });
            });
            attemptedToTrackItemIndexes.current = [];
            trackNullSearchRelatedItemsImpressions({ index });
        }
    };

    return hasRelatedSearchItems ? (
        <>
            <div className={styles.followHeader}>
                {isClient && (
                    <SbSharedNullSearchRelatedItemsFollowLazy
                        user={user}
                        itemSearch={itemSearch}
                        searchTerm={searchTerm}
                    />
                )}
            </div>
            <SbSharedSearchSuggestions
                searchTerm={searchTerm}
                itemSearch={itemSearch}
                className={styles.searchSuggestions}
            />
            <div className={styles.container}>
                <div className={styles.tilesHeader}>
                    <FormattedMessage
                        id="SbSharedNullSearchRelatedItems.tilesTitle"
                        defaultMessage="Showing results matching fewer words"
                    />
                </div>

                <FavoritesProvider
                    userId={userId}
                    disable={!isClient}
                    environment={relay.environment}
                    fetchFolder={shouldFetchFolder}
                    location={locations.SEARCH_BROWSE}
                    itemIds={nodes.map(({ node }) => node?.item?.serviceId)}
                >
                    {({ props: favorites }) => (
                        <QuickViewProvider>
                            {({ quickView }) => (
                                <SbSharedItemTrackingProvider
                                    viewer={viewer}
                                    itemSearch={relatedSearchResults}
                                    items={nodes?.map(item => item?.node?.item || null) || null}
                                    isRelatedSearchItems
                                >
                                    {({ fireItemImpressionTracking }) => (
                                        <div className={styles.tilesWrapper}>
                                            {nodes.map(({ node: { item } }, index) => {
                                                return (
                                                    <SharedItemTile
                                                        item={item}
                                                        user={null}
                                                        index={index}
                                                        isClient={isClient}
                                                        isMobile={isMobile}
                                                        key={item.serviceId}
                                                        favorites={favorites}
                                                        showSellerName={false}
                                                        showItemLocationBadge={
                                                            showItemLocationBadge
                                                        }
                                                        quickView={quickView}
                                                        measurementUnit={measurementUnit}
                                                        customClassName={styles.tile}
                                                        lazyLoadStartIndex={lazyLoadStartIndex}
                                                        imageLoadVerticalOffset={verticalOffset}
                                                        onContentVisible={() => {
                                                            onContentVisible(index);
                                                            fireItemImpressionTracking({
                                                                index,
                                                                itemId: item.serviceId,
                                                            });
                                                        }}
                                                        onClick={() =>
                                                            trackNullSearchRelatedItemClick({
                                                                index,
                                                                item,
                                                            })
                                                        }
                                                        viewer={viewer}
                                                        itemSearch={itemSearch}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}
                                </SbSharedItemTrackingProvider>
                            )}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </div>
        </>
    ) : null;
};

SbSharedNullSearchRelatedItemsComponent.propTypes = {
    user: PropTypes.object,
    isClient: PropTypes.bool,
    isMobile: PropTypes.bool,
    shouldFetchFolder: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    lazyLoadStartIndex: PropTypes.number,
    searchTerm: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    verticalOffset: PropTypes.number.isRequired,
};

export const SbSharedNullSearchRelatedItems = createFragmentContainer(
    SbSharedNullSearchRelatedItemsComponent,
    {
        itemSearch: graphql`
            fragment SbSharedNullSearchRelatedItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                relatedSearchResults {
                    edges {
                        node {
                            item {
                                title
                                serviceId
                                browseUrl
                                categoryCode
                                seller {
                                    serviceId
                                }
                                contemporaryTrackingString
                                ...SbSharedItemTrackingProvider_item
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                                ...shippingCostHelpers_item
                            }
                        }
                    }
                }
                ...useMeasurementUnit_itemSearch
                ...useSearchCorrections_itemSearch
                ...SbSharedSearchSuggestions_itemSearch
                ...SbSharedNullSearchRelatedItemsFollow_itemSearch
            }
        `,
        user: graphql`
            fragment SbSharedNullSearchRelatedItems_user on User {
                ...useMeasurementUnit_user
                ...SbSharedNullSearchRelatedItemsFollow_user
            }
        `,
        viewer: graphql`
            fragment SbSharedNullSearchRelatedItems_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
                ...SbSharedItemTrackingProvider_viewer
                ...SharedItemTile_viewer
            }
        `,
    }
);
